import { D } from '@makila/fiscal';
import { mergeOrder } from './orders.js';
const key = (t, y, training) => `${t}:${y}:${training}`;
/** Soma, por linha de origem, o que as NC já emitidas creditaram desse documento. */
export function creditedFrom(docs, seriesId, number) {
    const out = {};
    for (const d of docs) {
        if (d.docType !== 'NC' || d.origin?.seriesId !== seriesId || d.origin.number !== number)
            continue;
        for (const l of d.lines)
            if (l.originLine !== undefined)
                out[l.originLine] = new D(out[l.originLine] ?? 0).plus(l.quantity).toString();
    }
    return out;
}
const clone = (s) => ({
    ...s, series: new Map(s.series), outbox: [...s.outbox], documents: [...s.documents], catalog: new Map(s.catalog),
    sessions: new Map(s.sessions), movements: [...s.movements], orders: new Map(s.orders),
});
/**
 * Armazenamento em memória com a mesma semântica de transacção do SQLite:
 * escreve numa cópia e só a publica no fim; uma excepção deita a cópia fora.
 * Serve os testes e a demonstração no browser (sem persistência).
 */
export class MemoryTerminalStore {
    s = { series: new Map(), outbox: [], lastSeq: 0, ackedSeq: 0, documents: [], catalog: new Map(), cursor: 0, licence: null, sessions: new Map(), movements: [], openSessionId: null, lastZ: 0, orders: new Map(), ordersCursor: 0 };
    queue = Promise.resolve();
    /** Séries recebidas na activação. */
    seed(series) {
        for (const x of series)
            this.s.series.set(key(x.docType, x.year, x.training), x);
    }
    async transaction(fn) {
        // Uma transacção de cada vez, como o SQLite com escrita exclusiva.
        const run = this.queue.then(async () => {
            const w = clone(this.s);
            const tx = {
                getSeries: async (t, y, training) => w.series.get(key(t, y, training)) ?? null,
                putSeries: async (x) => { w.series.set(key(x.docType, x.year, x.training), x); },
                lastSeq: async () => w.lastSeq,
                appendOutbox: async (e) => {
                    if (e.seq !== w.lastSeq + 1)
                        throw new Error(`seq ${e.seq} fora de ordem (último ${w.lastSeq})`);
                    w.outbox.push(e);
                    w.lastSeq = e.seq;
                },
                saveDocument: async (d) => { w.documents.push(d); },
                openSession: async () => (w.openSessionId ? w.sessions.get(w.openSessionId) ?? null : null),
                putSession: async (x) => {
                    w.sessions.set(x.id, x);
                    if (x.status === 'open')
                        w.openSessionId = x.id;
                    else {
                        if (w.openSessionId === x.id)
                            w.openSessionId = null;
                        w.lastZ = Math.max(w.lastZ, x.zNumber ?? 0);
                    }
                },
                addMovement: async (m) => { w.movements.push(m); },
                lastZ: async () => w.lastZ,
                sessionDocuments: async (id) => w.documents.filter((d) => d.cashSessionId === id),
                sessionMovements: async (id) => w.movements.filter((m) => m.sessionId === id),
                creditedQuantities: async (seriesId, number) => creditedFrom(w.documents, seriesId, number),
                getOrder: async (id) => w.orders.get(id) ?? null,
                putOrder: async (o) => { w.orders.set(o.id, o); },
            };
            const out = await fn(tx);
            this.s = w;
            return out;
        });
        this.queue = run.catch(() => { });
        return run;
    }
    async pendingOutbox(limit) { return this.s.outbox.filter((e) => e.seq > this.s.ackedSeq).slice(0, limit); }
    async setAcked(seq) { this.s.ackedSeq = seq; }
    async catalogCursor() { return this.s.cursor; }
    async applyCatalog(items, nextCursor) {
        for (const i of items)
            this.s.catalog.set(`${i.type}:${i.id}`, i);
        this.s.cursor = Math.max(this.s.cursor, nextCursor);
    }
    async saveLicence(l) { this.s.licence = l; }
    async ordersCursor() { return this.s.ordersCursor; }
    async applyOrders(orders, nextCursor) {
        await this.transaction(async (tx) => { for (const o of orders) {
            const cur = await tx.getOrder(o.id);
            await tx.putOrder(cur ? mergeOrder(cur, o) : o);
        } });
        this.s.ordersCursor = Math.max(this.s.ordersCursor, nextCursor);
    }
    // Leituras para o ecrã e para os testes.
    get documents() { return this.s.documents; }
    get outbox() { return this.s.outbox; }
    get ackedSeq() { return this.s.ackedSeq; }
    get licence() { return this.s.licence; }
    get openSession() { return this.s.openSessionId ? this.s.sessions.get(this.s.openSessionId) ?? null : null; }
    get orders() { return [...this.s.orders.values()]; }
    catalogItems(type) { return [...this.s.catalog.values()].filter((i) => i.type === type); }
    series(t, y, training) { return this.s.series.get(key(t, y, training)); }
}
//# sourceMappingURL=memory-store.js.map